import * as startApi from "./api";
import { passLayoutForSave } from "./passBuilder";
import { buildFormPayload } from "./formBuilder";
import { buildFlyerRecord } from "./flyerBuilder";

// The records /start creates, each step idempotent: it looks at what already
// exists before creating, so a reload or a retry after a lost response (both
// common in in-app browsers) never makes a second card, form or flyer. The
// dashboard's own pages are not reused because each one navigates to /setup
// when it finishes.

// The card /start made, or the store's only card. /start is first-store
// onboarding, so a store normally has exactly one.
export function pickStartPass(passes = []) {
  return (
    passes.find((p) => p?.pass?.onboarding?.flow === "start") ||
    (passes.length === 1 ? passes[0] : null) ||
    null
  );
}

// The sign-up form for a card: the init form (kind 0) linked to it.
export function pickInitForm(forms = [], passId) {
  return (
    forms.find((f) => (f.kind === "init" || f.kind === 0) && String(f.pass?.id) === String(passId)) ||
    null
  );
}

export function isTimeZoneError(error) {
  const errors = error?.response?.data?.errors;
  return Array.isArray(errors) && errors.some((e) => /time zone/i.test(String(e)));
}

// Create or update the store. A time zone the server does not know is dropped
// and the request retried once (no time zone means UTC).
export async function saveStore({ storeId, fields, api = startApi }) {
  const send = (f) => (storeId ? api.updateStore(storeId, f) : api.createStore(f));
  try {
    return await send(fields);
  } catch (error) {
    if (fields.timeZone && isTimeZoneError(error)) {
      // eslint-disable-next-line no-unused-vars
      const { timeZone: _dropped, ...rest } = fields;
      return send(rest);
    }
    throw error;
  }
}

export function isDuplicateNameError(error) {
  const errors = error?.response?.data?.errors;
  return Array.isArray(errors) && errors.some((e) => /name has already been taken/i.test(String(e)));
}

export const OWNER_CARD_KEYS = ["start:ownerCardCreated", "start:ownerCardAdded"];

// Create the card, or update the one /start made before. `images` may be null
// on an update whose strip did not change: the server keeps the existing
// images when none are sent.
//
// A different card type replaces the card instead: the backend builds a card
// type's campaigns only when the card is created, so an updated type would keep
// the old type's rewards (the dashboard's editor deletes and recreates too).
// Returns { pass, recreated }.
export async function savePass({ storeId, store, formData, images, existingPass, api = startApi }) {
  const layoutData = passLayoutForSave(formData, store);
  const payload = {
    layoutData,
    stampIcon: images?.stampIcon,
    backgroundImages: images?.backgroundImages,
    previewImage: images?.previewImage,
  };
  // A card from an attempt that failed after creating it (or whose response
  // was lost) is reused rather than duplicated.
  const current = existingPass || pickStartPass(await api.listPasses(storeId));
  if (current) {
    const currentType = current.pass?.type || "stamp";
    if (currentType === formData.type) {
      return { pass: await api.updatePass(storeId, current.id, payload), recreated: false };
    }
    try {
      await api.deletePass(storeId, current.id);
    } catch (error) {
      if (error?.response?.status !== 404) throw error;
    }
    return { pass: await api.createPass(storeId, payload), recreated: true };
  }
  try {
    return { pass: await api.createPass(storeId, payload), recreated: false };
  } catch (error) {
    // The create went through but the response was lost: use that card.
    if (isDuplicateNameError(error)) {
      const found = pickStartPass(await api.listPasses(storeId));
      if (found) return { pass: await api.updatePass(storeId, found.id, payload), recreated: false };
    }
    throw error;
  }
}

export async function saveForm({ storeId, passId, draft, t, lang, colors, api = startApi }) {
  const payload = buildFormPayload({
    passId,
    draft,
    t,
    lang,
    backgroundColor: colors.backgroundColor,
    textColor: colors.textColor,
  });
  const existing = pickInitForm(await api.listForms(storeId), passId);
  if (existing) return api.updateForm(storeId, existing.id, payload);
  return api.createForm(storeId, payload);
}

// Makes sure the store has a flyer record (without a PDF if need be). Returns
// the flyer. Its PDF is attached separately by attachFlyerPdf.
export async function ensureFlyerRecord({ storeId, formId, paperSize, colors, t, api = startApi }) {
  const flyers = await api.listFlyers(storeId);
  const existing = flyers.find((f) => String(f.form?.id) === String(formId)) || flyers[0];
  if (existing) return existing;
  return api.createFlyer(
    storeId,
    buildFlyerRecord({ formId, paperSize, backgroundColor: colors.backgroundColor, textColor: colors.textColor, t }),
  );
}

export async function attachFlyerPdf({ storeId, flyer, formId, paperSize, colors, t, pdf, api = startApi }) {
  const record = buildFlyerRecord({
    formId: formId || flyer.form?.id,
    paperSize: paperSize || flyer.paper_size,
    backgroundColor: colors.backgroundColor,
    textColor: colors.textColor,
    t,
  });
  return api.updateFlyer(storeId, flyer.id, record, pdf);
}
